import React, { useState } from 'react';
import {
  Users,
  Package,
  FileText,
  Coins,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { customerService } from '@/services/customerService';
import { itemService } from '@/services/itemService';
import { transactionService } from '@/services/transactionService';
import MonthlySalesChart from '@/components/MonthlySalesChart';
import AddQuotationModal from '@/components/AddQuotationModal';
import AddClientModal from '@/components/AddClientModal';
import AddSaleModal from '@/components/AddSaleModal';

interface StatCard {
  title: string;
  value: string;
  change: number;
  icon: React.ElementType;
  color: string;
}


const getColorClasses = (color: string) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return colors[color as keyof typeof colors] || colors.blue;
};

const getStatusColor = (status: string) => {
  switch (status) {
    // Sale statuses
    case 'PAGADA':
      return 'badge-success';
    case 'PENDIENTE':
      return 'badge-warning';
    case 'REEMBOLSADA':
      return 'badge-info';
    case 'ANULADA':
      return 'badge-danger';
    // Quotation statuses
    case 'CREADA':
      return 'badge-gray';
    case 'ENVIADA':
      return 'badge-info';
    case 'ACEPTADA':
      return 'badge-success';
    case 'RECHAZADA':
      return 'badge-danger';
    case 'VENCIDA':
      return 'badge-warning';
    case 'FINALIZADA':
      return 'badge-success';
    default:
      return 'badge-gray';
  }
};

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [transactionFilter, setTransactionFilter] = useState<'AMBAS' | 'VENTA' | 'COTIZACION'>('AMBAS');

  // Query for customer stats
  const { data: customerStats } = useQuery({
    queryKey: ['customerStats'],
    queryFn: () => customerService.getStats(),
    staleTime: 30000, // 30 seconds
  });

  // Query for item stats
  const { data: itemStats } = useQuery({
    queryKey: ['itemStats'],
    queryFn: () => itemService.getStats(),
    staleTime: 30000, // 30 seconds
  });

  // Query for transaction stats
  const { data: transactionStats } = useQuery({
    queryKey: ['transactionStats'],
    queryFn: () => transactionService.getStats(),
    staleTime: 30000, // 30 seconds
  });

  // Query for recent transactions
  const { data: recentTransactions = [] } = useQuery({
    queryKey: ['recentTransactions', transactionFilter],
    queryFn: () => transactionService.getRecentTransactions(5, transactionFilter === 'AMBAS' ? undefined : transactionFilter),
    staleTime: 30000, // 30 seconds
  });

  // Create dynamic stats array with real customer data
  const stats: StatCard[] = [
    {
      title: 'Total Clientes',
      value: customerStats?.total?.toString() || '0',
      change: 0, // TODO: Calculate change vs previous month
      icon: Users,
      color: 'blue',
    },
    {
      title: 'Productos Activos',
      value: itemStats?.active?.toString() || '0',
      change: 2,
      icon: Package,
      color: 'green',
    },
    {
      title: 'Cotizaciones Enviadas',
      value: transactionStats?.cotizaciones_enviadas?.toString() || '0',
      change: -3,
      icon: FileText,
      color: 'yellow',
    },
    {
      title: 'Ventas del Mes',
      value: transactionStats?.total_amount ? transactionService.formatCurrency(transactionStats.total_amount) : '$0',
      change: 18,
      icon: Coins,
      color: 'emerald',
    },
    {
      title: 'Citas Programadas',
      value: '15',
      change: 5,
      icon: Calendar,
      color: 'purple',
    },
  ];

  return (
    <div className="responsive-padding space-y-6">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          ¡Bienvenido, {user?.first_name}!
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          Aquí tienes un resumen de la actividad de tu empresa.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
        {stats.map((stat) => (
          <div key={stat.title} className="card">
            <div className="card-body p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{stat.title}</p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`p-2 sm:p-3 rounded-full ${getColorClasses(stat.color)} flex-shrink-0`}>
                  <stat.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
              </div>
              <div className="mt-3 sm:mt-4 flex items-center">
                {stat.change > 0 ? (
                  <ArrowUpRight className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                ) : (
                  <ArrowDownRight className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                )}
                <span className={`text-xs sm:text-sm ml-1 ${
                  stat.change > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {Math.abs(stat.change)}%
                </span>
                <span className="text-xs sm:text-sm text-gray-500 ml-1 hidden sm:inline">vs mes anterior</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Chart Card */}
        <div className="lg:col-span-2 card">
          <div className="card-header">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">Ventas</h3>
          </div>
          <div className="card-body">
            <MonthlySalesChart type="line" height={256} />
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="card">
          <div className="card-header">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">Transacciones Recientes</h3>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 whitespace-nowrap">Filtrar:</label>
                <select
                  value={transactionFilter}
                  onChange={(e) => setTransactionFilter(e.target.value as 'AMBAS' | 'VENTA' | 'COTIZACION')}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="AMBAS">Todas</option>
                  <option value="VENTA">Ventas</option>
                  <option value="COTIZACION">Cotizaciones</option>
                </select>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="divide-y divide-gray-200">
              {recentTransactions.length > 0 ? recentTransactions.map((transaction) => (
                <div key={transaction.id} className="p-3 sm:p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1">
                        <span className="text-xs sm:text-sm font-medium text-gray-900">
                          {transaction.numero}
                        </span>
                        <span className={`badge ${getStatusColor(transaction.estado)} mt-1 sm:mt-0 self-start`}>
                          {transaction.tipo === 'COTIZACION' 
                            ? transactionService.formatQuotationStatus(transaction.estado)
                            : transactionService.formatSaleStatus(transaction.estado)
                          }
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">
                        {transaction.cliente?.name || 'Cliente no disponible'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(transaction.created_at).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="text-xs sm:text-sm font-medium text-gray-900">
                        {transactionService.formatCurrency(transaction.total)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {transaction.tipo === 'COTIZACION' ? 'Cotización' : 'Venta'}
                      </p>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="p-6 text-center text-gray-500">
                  <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No hay transacciones recientes</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">Acciones Rápidas</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <button
              className="btn-primary text-sm sm:text-base py-3"
              onClick={() => setIsQuotationModalOpen(true)}
            >
              + Nueva Cotización
            </button>
            <button
              className="btn-success text-sm sm:text-base py-3"
              onClick={() => setIsSaleModalOpen(true)}
            >
              + Nueva Venta
            </button>
            <button
              className="btn-secondary text-sm sm:text-base py-3"
              onClick={() => setIsClientModalOpen(true)}
            >
              + Agregar Cliente
            </button>
            <button className="btn-outline text-sm sm:text-base py-3">
              📊 Ver Reportes
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddQuotationModal
        isOpen={isQuotationModalOpen}
        onClose={() => setIsQuotationModalOpen(false)}
      />
      
      <AddClientModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
      />

      <AddSaleModal
        isOpen={isSaleModalOpen}
        onClose={() => setIsSaleModalOpen(false)}
      />
    </div>
  );
};